<script setup lang="ts">
const config = useRuntimeConfig()
const openFeature = useOpenFeature()

defineProps<{
  rail: boolean
}>()

const color = computed(() =>
  config.public.datadog.env.startsWith('prd') ? 'primary' : 'warning',
)
const version = config.public.datadog.version
// Git shas are 40 chars; the ends are enough to recognize a build.
const shortVersion = computed(() =>
  /^[0-9a-f]{40}$/.test(version)
    ? `${version.slice(0, 3)}…${version.slice(-3)}`
    : version,
)
const year = computed(() => openFeature.getNumber('current_film_festival'))
const lastYear = ref(localStorage.getItem('sato48:lastYear') === 'true')

const onPreviousYear = () => {
  localStorage.setItem('sato48:lastYear', 'true')
  lastYear.value = true
  openFeature.mergeContext({
    lastYear: true
  })
}

const onCurrentYear = () => {
  localStorage.removeItem('sato48:lastYear')
  lastYear.value = false
  openFeature.mergeContext({
    lastYear: false
  })
}
</script>

<template>
  <div v-if="openFeature.getBool('show_revision')" class="px-2 pb-2">
    <v-menu :location="rail ? 'end' : 'top'" :offset="8">
      <template #activator="{ props: menuProps }">
        <v-tooltip
          :disabled="!rail"
          :text="`rev ${version} · env ${config.public.datadog.env}`"
          location="end"
        >
          <template #activator="{ props: tooltipProps }">
            <button
              v-bind="{ ...menuProps, ...tooltipProps }"
              class="version"
              :class="[`bg-${color}`, { rail }]"
              aria-label="Build info and festival year"
            >
              <template v-if="rail">
                <span class="mono">{{ year }}</span>
              </template>
              <template v-else>
                <span class="field">
                  <span class="label">rev</span>
                  <span class="mono text-truncate" :title="version">{{ shortVersion }}</span>
                </span>
                <span class="field fixed">
                  <span class="label">year</span>
                  <span class="mono">{{ year }}</span>
                </span>
                <span class="field">
                  <span class="label">env</span>
                  <span class="mono text-truncate">{{ config.public.datadog.env }}</span>
                </span>
              </template>
            </button>
          </template>
        </v-tooltip>
      </template>
      <v-list density="compact" nav slim>
        <v-list-subheader>Festival year</v-list-subheader>
        <v-list-item
          title="Current year"
          :append-icon="lastYear ? undefined : 'mdi-check'"
          @click="onCurrentYear"
        />
        <v-list-item
          title="Previous year"
          :append-icon="lastYear ? 'mdi-check' : undefined"
          @click="onPreviousYear"
        />
      </v-list>
    </v-menu>
  </div>
</template>

<style scoped>
.version {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  height: 26px;
  padding: 0 8px;
  border-radius: 6px;
  font-size: 0.6875rem;
  line-height: 1;
}

.version.rail {
  justify-content: center;
  width: 40px;
  margin-inline: auto;
  padding: 0;
  font-weight: 700;
}

.field {
  display: flex;
  align-items: center;
  gap: 3px;
  min-width: 0;
}

.field.fixed {
  flex-shrink: 0;
}

.label {
  font-size: 0.5625rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.75;
}

.mono {
  font-family: var(--font-mono);
}
</style>
